import React from 'react';
import { Link as RouterLink, LinkProps as RouterLinkProps } from 'react-router-dom';
import { Icon as ReactIcon } from '@a-ui/react';
import useClassNames, { IClassNames } from '../../hooks/classNames.hooks';
import isExternalUrl from '../../util/isExternalUrl';
import './style.scss';

interface LinkProps extends Omit<RouterLinkProps, 'className'>, IClassNames {
  to: string;
  icon?: string;
  iconPlacement?: 'left' | 'right';
  iconScreenReaderText?: string;
  disabled?: boolean;
}

const Link: React.FC<LinkProps> = ({
  to,
  children,
  classNames,
  icon,
  iconPlacement = 'right',
  iconScreenReaderText = '',
  disabled,
  ...props
}) => {
  const isExternal = props.target === '_blank' || isExternalUrl(to.toString());
  const { className } = useClassNames(icon && `has-icon-${iconPlacement}`, classNames);
  const content = (
    <>
      {icon && iconPlacement === 'left' && (
        <ReactIcon name={icon} screenReaderText={iconScreenReaderText} />
      )}
      {children}
      {icon && iconPlacement === 'right' && (
        <ReactIcon name={icon} screenReaderText={iconScreenReaderText} />
      )}
    </>
  );

  if (disabled) {
    return (
      <span className={className} {...props} aria-disabled>
        {content}
      </span>
    );
  }

  if (isExternal) {
    return (
      <a href={to} className={className} {...props}>
        {content}
      </a>
    );
  }

  return (
    <RouterLink to={to} className={className} {...props}>
      {content}
    </RouterLink>
  );
};

export default Link;
